import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, PencilAltIcon, TrashIcon, XCircleIcon, ClockIcon } from '@heroicons/react/outline';

import Table from '../../../../components/form/table';
import { getPerfectDate } from '../../../../utils';

const formatBytes = (value) => {
  const size = Number(value || 0);
  if (!size) {
    return '';
  }
  if (size < 1024) {
    return `${size} B`;
  }
  return `${(size / 1024).toFixed(1)} KB`;
};

const formatDate = (value) => {
  if (!value) {
    return '';
  }
  return getPerfectDate(new Date(value));
};

const shortChecksum = (value) => {
  if (!value) {
    return '';
  }
  return value.length > 16 ? `${value.slice(0, 16)}...` : value;
};

const FirmwareReleasesTable = ({ tableData, onItemEdit, onItemDelete, onItemToggleActive, onShowLogs }) => {
  const { t } = useTranslation();

  const items = tableData.map((tableItem) => {
    const attributes = tableItem.attributes;

    return {
      id: tableItem.id,
      version: attributes.version,
      target_type: attributes.target_type,
      active: attributes.active,
      serial_targets: attributes.serial_targets || [],
      serial_targets_count: attributes.serial_targets_count || 0,
      model_target: attributes.device_model_name,
      filename: attributes.filename,
      file_size: attributes.file_size,
      file_sha256: attributes.file_sha256,
      content_type: attributes.content_type,
      created_at: attributes.created_at,
      updated_at: attributes.updated_at,
    };
  });

  const columns = [
    {
      label: t('firmware_version'),
      prop: 'version',
      labelClassName: 'w-full lg:w-2/12',
      className: 'w-full lg:w-2/12 flex-shrink-0',
      render: ({ version, active }) => (
        <div>
          <div className="font-medium text-gray-900">{version}</div>
          <div className={`flex items-center text-xs mt-1 ${active ? 'text-green-600' : 'text-gray-500'}`}>
            {active ? <CheckCircleIcon className="w-4 h-4 mr-1" /> : <XCircleIcon className="w-4 h-4 mr-1" />}
            {active ? t('active') : t('no_active')}
          </div>
        </div>
      ),
    },
    {
      label: t('firmware_target'),
      prop: 'target',
      labelClassName: 'w-full lg:w-3/12',
      className: 'w-full lg:w-3/12 flex-shrink-0',
      render: ({ target_type, serial_targets, serial_targets_count, model_target }) => (
        <div>
          <div className="font-medium text-gray-900">{t(`firmware_target_${target_type}`)}</div>
          {target_type === 'serial' ? (
            <div className="text-gray-500 break-words">
              {serial_targets_count} · {serial_targets.join(', ')}
            </div>
          ) : (
            <div className="text-gray-500">{model_target || t('no_selected')}</div>
          )}
        </div>
      ),
    },
    {
      label: t('firmware_file'),
      prop: 'file',
      labelClassName: 'w-full lg:w-3/12',
      className: 'w-full lg:w-3/12 flex-shrink-0',
      render: ({ filename, file_size, file_sha256 }) => (
        <div>
          <div className="font-medium text-gray-900 break-all">{filename}</div>
          <div className="text-gray-500">{formatBytes(file_size)}</div>
          <div className="text-gray-500 break-all" title={file_sha256}>{shortChecksum(file_sha256)}</div>
        </div>
      ),
    },
    {
      label: t('updated_at'),
      prop: 'updated_at',
      labelClassName: 'w-full lg:w-2/12',
      className: 'w-full lg:w-2/12 flex-shrink-0',
      render: ({ created_at, updated_at }) => (
        <div>
          <div className="text-gray-900">{formatDate(updated_at)}</div>
          <div className="text-gray-500">{formatDate(created_at)}</div>
        </div>
      ),
    },
    {
      label: t('actions'),
      prop: 'actions',
      labelClassName: 'invisible w-full lg:w-2/12',
      className: 'w-full lg:w-2/12 flex-shrink-0',
      render: (row) => (
        <div className="flex space-x-2 justify-end">
          <button
            type="button"
            onClick={() => onShowLogs(row.id)}
            aria-label={`${t('firmware_delivery_log')} ${row.version}`}
            className="text-slate-600 hover:text-slate-900 cursor-pointer w-8 h-8 bg-slate-100 hover:bg-slate-200 flex items-center justify-center rounded-sm"
          >
            <ClockIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onItemToggleActive(row.id, row.active)}
            aria-label={`${row.active ? t('deactivate') : t('activate')} ${row.version}`}
            className="text-indigo-600 hover:text-indigo-900 cursor-pointer w-8 h-8 bg-blue-100 hover:bg-blue-200 flex items-center justify-center rounded-sm"
          >
            {row.active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={() => onItemEdit(row.id)}
            aria-label={`${t('edit')} ${row.version}`}
            className="text-yellow-600 hover:text-yellow-900 cursor-pointer w-8 h-8 bg-yellow-100 hover:bg-yellow-200 flex items-center justify-center rounded-sm"
          >
            <PencilAltIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onItemDelete(row.id)}
            aria-label={`${t('delete')} ${row.version}`}
            className="text-red-600 hover:text-red-900 cursor-pointer w-8 h-8 bg-pink-100 hover:bg-pink-200 flex items-center justify-center rounded-sm"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Table
      border={true}
      columns={columns}
      data={items}
      emptyText={t('no_data')}
    />
  );
};

export default FirmwareReleasesTable;
